import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { dictionaryService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AddWordDialog from './AddWordDialog';

const ENTRY_FIELDS = [
  { key: 'grammar', label: 'Grammaire', multiline: false },
  { key: 'french', label: 'Français', multiline: true },
  { key: 'english', label: 'Anglais', multiline: true },
  { key: 'spanish', label: 'Espagnol', multiline: true },
  { key: 'german', label: 'Allemand (texte original Dinkelacker)', multiline: true },
  { key: 'note', label: 'Note', multiline: true },
];

const EXAMPLE_FIELDS = [
  { key: 'duala', label: 'Duala' },
  { key: 'francais', label: 'Français' },
  { key: 'anglais', label: 'Anglais' },
  { key: 'espagnol', label: 'Espagnol' },
  { key: 'allemand', label: 'Allemand' },
];

const parseExamples = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw) || []; } catch (_) { return []; }
};

const PAGE_SIZE = 20;

const STATUS_LABEL = {
  nouveau: { label: 'Nouveau', color: 'warning' },
  traite: { label: 'Traité', color: 'success' },
  rejete: { label: 'Rejeté', color: 'default' },
};

// part : 'words' (mots suggérés) ou 'corrections' (anciens signalements en texte libre) quand la page est
// intégrée dans « Corrections dictionnaire » ; sans part, les deux blocs.
function Suggestions({ part }) {
  const [suggestions, setSuggestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [corrections, setCorrections] = useState([]);
  const [totalCorrections, setTotalCorrections] = useState(0);
  const [correctionsPage, setCorrectionsPage] = useState(1);
  const [loadingCorrections, setLoadingCorrections] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === 'super_admin';

  // Suggestion de mot en cours d'ajout au dictionnaire (super admin)
  const [addTarget, setAddTarget] = useState(null);

  // Correction en cours d'application + brouillon editable de l'entree visee.
  const [applyTarget, setApplyTarget] = useState(null);
  const [draft, setDraft] = useState(null);
  const [applying, setApplying] = useState(false);

  const openApply = (correction) => {
    const e = correction.entry;
    setDraft({
      grammar: e.grammar || '',
      french: e.french || '',
      english: e.english || '',
      spanish: e.spanish || '',
      german: e.german || '',
      note: e.note || '',
      examples: parseExamples(e.examples).map(ex => ({
        duala: ex.duala || '',
        francais: ex.francais || '',
        anglais: ex.anglais || '',
        espagnol: ex.espagnol || '',
        allemand: ex.allemand || '',
      })),
    });
    setApplyTarget(correction);
  };

  const closeApply = () => {
    if (applying) return;
    setApplyTarget(null);
    setDraft(null);
  };

  const submitApply = async () => {
    setApplying(true);
    try {
      await dictionaryService.updateEntry(applyTarget.entry.id, {
        ...draft,
        correction_id: applyTarget.id,
      });
      setCorrections(prev => prev.map(c => (c.id === applyTarget.id ? { ...c, status: 'traite' } : c)));
      enqueueSnackbar('Entrée modifiée dans le dictionnaire', { variant: 'success' });
      setApplyTarget(null);
      setDraft(null);
    } catch (err) {
      console.error('Erreur application correction:', err);
      const message = err.response && err.response.data && err.response.data.error;
      enqueueSnackbar(message || 'Erreur lors de la modification de l\'entrée', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const setDraftField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const setExampleField = (index, key, value) => setDraft(prev => ({
    ...prev,
    examples: prev.examples.map((ex, i) => (i === index ? { ...ex, [key]: value } : ex)),
  }));

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dictionaryService.getSuggestions({ page, limit: PAGE_SIZE });
      setSuggestions(res.data.suggestions);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Erreur chargement suggestions:', err);
      enqueueSnackbar('Erreur lors du chargement des suggestions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, enqueueSnackbar]);

  const loadCorrections = useCallback(async () => {
    setLoadingCorrections(true);
    try {
      const res = await dictionaryService.getCorrections({ page: correctionsPage, limit: PAGE_SIZE });
      setCorrections(res.data.corrections);
      setTotalCorrections(res.data.total);
    } catch (err) {
      console.error('Erreur chargement corrections:', err);
      enqueueSnackbar('Erreur lors du chargement des corrections signalées', { variant: 'error' });
    } finally {
      setLoadingCorrections(false);
    }
  }, [correctionsPage, enqueueSnackbar]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    loadCorrections();
  }, [loadCorrections]);

  const updateStatus = async (id, status) => {
    try {
      await dictionaryService.updateSuggestionStatus(id, status);
      setSuggestions(prev => prev.map(s => (s.id === id ? { ...s, status } : s)));
    } catch (err) {
      console.error('Erreur maj statut suggestion:', err);
      enqueueSnackbar('Erreur lors de la mise à jour', { variant: 'error' });
    }
  };

  const updateCorrectionStatus = async (id, status) => {
    try {
      await dictionaryService.updateCorrectionStatus(id, status);
      setCorrections(prev => prev.map(c => (c.id === id ? { ...c, status } : c)));
    } catch (err) {
      console.error('Erreur maj statut correction:', err);
      enqueueSnackbar('Erreur lors de la mise à jour', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: part ? 0 : 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {part !== 'corrections' && (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">💡 Suggestions de mots du dictionnaire</Typography>
            <Tooltip title="Actualiser">
              <IconButton onClick={loadSuggestions} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : suggestions.length === 0 ? (
            <Typography color="text.secondary">Aucune suggestion pour le moment.</Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Mot</TableCell>
                      <TableCell>Langue</TableCell>
                      <TableCell>Précision</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map((s) => (
                      <TableRow key={s.id} sx={{ bgcolor: s.status === 'nouveau' ? 'action.hover' : 'inherit' }}>
                        <TableCell>{new Date(s.created_at).toLocaleString('fr-FR')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.word}</TableCell>
                        <TableCell>{s.language === 'francais' ? 'Français' : 'Duala'}</TableCell>
                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>{s.note || '-'}</TableCell>
                        <TableCell>{s.contact_email || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={STATUS_LABEL[s.status]?.label || s.status}
                            size="small"
                            color={STATUS_LABEL[s.status]?.color || 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isSuperAdmin && s.status !== 'traite' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => setAddTarget(s)}
                              sx={{ textTransform: 'none', fontWeight: 700, mr: 0.5, whiteSpace: 'nowrap' }}
                            >
                              Ajouter au dictionnaire
                            </Button>
                          )}
                          {s.status !== 'traite' && (
                            <Tooltip title="Marquer comme traité sans l'ajouter">
                              <IconButton size="small" onClick={() => updateStatus(s.id, 'traite')}>
                                <CheckIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {s.status !== 'rejete' && (
                            <Tooltip title="Rejeter">
                              <IconButton size="small" onClick={() => updateStatus(s.id, 'rejete')}>
                                <CloseIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {total > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(total / PAGE_SIZE)}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {part !== 'words' && (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">🚩 Corrections signalées sur des entrées existantes</Typography>
            <Tooltip title="Actualiser">
              <IconButton onClick={loadCorrections} size="small" disabled={loadingCorrections}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {loadingCorrections ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : corrections.length === 0 ? (
            <Typography color="text.secondary">Aucune correction signalée pour le moment.</Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Entrée</TableCell>
                      <TableCell>Dans le dictionnaire aujourd'hui</TableCell>
                      <TableCell>Correction proposée</TableCell>
                      <TableCell>Précision</TableCell>
                      <TableCell>Signalé par</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {corrections.map((c) => (
                      <TableRow key={c.id} sx={{ bgcolor: c.status === 'nouveau' ? 'action.hover' : 'inherit' }}>
                        <TableCell>{new Date(c.created_at).toLocaleString('fr-FR')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontStyle: 'italic' }}>
                          {c.entry ? c.entry.duala : `#${c.entry_id}`}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          {c.entry ? (
                            <>
                              {c.entry.grammar && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                  {c.entry.grammar}
                                </Typography>
                              )}
                              <Typography variant="body2">{c.entry.french}</Typography>
                              {c.entry.note && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  Note : {c.entry.note}
                                </Typography>
                              )}
                              {c.entry.internal_note && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                  Note interne : {c.entry.internal_note}
                                </Typography>
                              )}
                            </>
                          ) : '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{c.proposed_correction}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>{c.note || '-'}</TableCell>
                        <TableCell>
                          {c.submitter ? `${c.submitter.first_name || ''} ${c.submitter.last_name || ''}`.trim() || c.submitter.email : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={STATUS_LABEL[c.status]?.label || c.status}
                            size="small"
                            color={STATUS_LABEL[c.status]?.color || 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isSuperAdmin && c.entry && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<EditNoteIcon />}
                              onClick={() => openApply(c)}
                              sx={{ textTransform: 'none', fontWeight: 700, mr: 0.5, whiteSpace: 'nowrap' }}
                            >
                              Corriger l'entrée
                            </Button>
                          )}
                          {c.status !== 'traite' && (
                            <Tooltip title="Marquer comme traité sans modifier l'entrée">
                              <IconButton size="small" onClick={() => updateCorrectionStatus(c.id, 'traite')}>
                                <CheckIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.status !== 'rejete' && (
                            <Tooltip title="Rejeter">
                              <IconButton size="small" onClick={() => updateCorrectionStatus(c.id, 'rejete')}>
                                <CloseIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalCorrections > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(totalCorrections / PAGE_SIZE)}
                    page={correctionsPage}
                    onChange={(_, value) => setCorrectionsPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {addTarget && (
        <AddWordDialog
          suggestion={addTarget}
          onClose={() => setAddTarget(null)}
          onAdded={() => {
            setSuggestions((prev) => prev.map((x) => (x.id === addTarget.id ? { ...x, status: 'traite' } : x)));
            setAddTarget(null);
            enqueueSnackbar('Mot ajouté au dictionnaire (non vérifié)', { variant: 'success' });
          }}
        />
      )}

      <Dialog open={!!applyTarget} onClose={closeApply} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle>
          Appliquer la correction — <em>{applyTarget && applyTarget.entry ? applyTarget.entry.duala : ''}</em>
        </DialogTitle>
        <DialogContent dividers>
          {applyTarget && draft && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Correction proposée pour « {applyTarget.entry.duala} » :</strong> {applyTarget.proposed_correction}
                {applyTarget.note ? `\n\nPrécision : ${applyTarget.note}` : ''}
              </Alert>
              <Typography variant="caption" color="text.secondary">
                Modifiez les champs concernés ci-dessous puis enregistrez : l&apos;entrée est mise à jour immédiatement dans le
                dictionnaire, la modification est conservée lors des prochains imports, et la correction passe en « Traité ».
              </Typography>

              {ENTRY_FIELDS.map(({ key, label, multiline }) => (
                <TextField
                  key={key}
                  label={label}
                  value={draft[key]}
                  onChange={(e) => setDraftField(key, e.target.value)}
                  multiline={multiline}
                  minRows={multiline ? 2 : undefined}
                  fullWidth
                />
              ))}

              {draft.examples.length > 0 && (
                <>
                  <Divider textAlign="left">Exemples</Divider>
                  {draft.examples.map((ex, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography variant="subtitle2">Exemple {i + 1}</Typography>
                      {EXAMPLE_FIELDS.map(({ key, label }) => (
                        <TextField
                          key={key}
                          label={label}
                          value={ex[key]}
                          onChange={(e) => setExampleField(i, key, e.target.value)}
                          multiline
                          size="small"
                          fullWidth
                        />
                      ))}
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApply} disabled={applying}>Annuler</Button>
          <Button variant="contained" onClick={submitApply} disabled={applying || !(draft && draft.french.trim())}>
            {applying ? 'Enregistrement...' : 'Enregistrer dans le dictionnaire'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Suggestions;
